/**
 * Auth Cache Service
 * 
 * Caches auth state to prevent excessive API calls (2000+ in 5 mins)
 * 
 * Cache is invalidated only when:
 * - User signs out
 * - Cache expires (default 5 minutes)
 * - Settings changes (security-critical operations)
 * - Token refresh required
 * 
 * Benefits:
 * - Fast navigation without auth checks
 * - Reduced API calls
 * - Better performance
 */

import { safeJsonParse } from './utils';

interface CachedUser {
  id: string;
  email: string;
  displayName?: string;
  avatarUrl?: string;
}

interface AuthCacheEntry {
  user: CachedUser | null;
  timestamp: number;
  expiresAt: number;
}

class AuthCacheService {
  private cache: AuthCacheEntry | null = null;
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly STORAGE_KEY = 'naible_auth_cache';
  private readonly CACHE_VERSION = '1.0.0';
  
  constructor() {
    // Load from localStorage on init
    this.loadFromStorage();
  }

  /**
   * Get cached user
   * Returns null if cache is invalid/expired
   */
  getCachedUser(): CachedUser | null {
    if (!this.cache) {
      return null;
    }

    // Check if cache is expired
    if (Date.now() > this.cache.expiresAt) {
      console.log('⏰ Auth cache expired');
      this.invalidate();
      return null;
    }

    console.log('✅ Using cached auth (no API call needed)');
    return this.cache.user;
  }

  /**
   * Set cached user
   * @param user User object to cache
   * @param ttl Time to live in milliseconds (default 5 minutes)
   */
  setCachedUser(user: CachedUser | null, ttl: number = this.DEFAULT_TTL): void {
    const now = Date.now();
    this.cache = {
      user,
      timestamp: now,
      expiresAt: now + ttl
    };

    // Persist to localStorage
    this.saveToStorage();
    
    console.log('💾 Auth cached for', ttl / 1000 / 60, 'minutes');
  }

  /**
   * Invalidate cache (force next auth check to call API)
   * Call this on:
   * - Logout
   * - Settings changes
   * - Token refresh errors
   */
  invalidate(): void {
    console.log('🗑️ Auth cache invalidated');
    this.cache = null;
    this.clearStorage();
  }

  /**
   * Check if cache is valid
   */
  isValid(): boolean {
    if (!this.cache) return false;
    return Date.now() <= this.cache.expiresAt;
  }

  /**
   * Get cache age in seconds
   */
  getCacheAge(): number {
    if (!this.cache) return -1;
    return (Date.now() - this.cache.timestamp) / 1000;
  }

  /**
   * Get time until expiry in seconds
   */
  getTimeUntilExpiry(): number {
    if (!this.cache) return -1;
    return Math.max(0, (this.cache.expiresAt - Date.now()) / 1000);
  }

  /**
   * Refresh cache TTL (extend expiry time)
   * Useful for active users to keep them logged in
   */
  refreshTTL(ttl: number = this.DEFAULT_TTL): void {
    if (!this.cache || !this.cache.user) return;
    
    const now = Date.now();
    this.cache.timestamp = now;
    this.cache.expiresAt = now + ttl;
    this.saveToStorage();
    
    console.log('🔄 Auth cache TTL refreshed');
  }

  /**
   * Save cache to localStorage
   */
  private saveToStorage(): void {
    try {
      if (!this.cache) {
        this.clearStorage();
        return;
      }

      const data = {
        version: this.CACHE_VERSION,
        cache: this.cache
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save auth cache:', error);
    }
  }

  /**
   * Load cache from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return;

      const data = safeJsonParse(stored);
      
      // Check version compatibility
      if (data.version !== this.CACHE_VERSION) {
        console.log('⚠️ Auth cache version mismatch - clearing');
        this.clearStorage();
        return;
      }

      // Check if expired
      if (Date.now() > data.cache.expiresAt) {
        console.log('⚠️ Stored auth cache expired - clearing');
        this.clearStorage();
        return;
      }

      this.cache = data.cache;
      console.log('✅ Auth cache loaded from storage');
    } catch (error) {
      console.error('Failed to load auth cache:', error);
      this.clearStorage();
    }
  }

  /**
   * Clear cache from localStorage
   */
  private clearStorage(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear auth cache:', error);
    }
  }

  /**
   * Get cache stats for debugging
   */
  getStats() {
    return {
      isValid: this.isValid(),
      hasUser: !!this.cache?.user,
      cacheAge: this.getCacheAge(),
      timeUntilExpiry: this.getTimeUntilExpiry(),
      userId: this.cache?.user?.id,
      userEmail: this.cache?.user?.email
    };
  }
}

// Export singleton instance
export const authCache = new AuthCacheService();

/**
 * Helper to determine if auth API call should be made
 * 
 * @param requireFresh Force fresh API call (for security-critical operations)
 * @returns true if API call should be made, false if cached value can be used
 */
export function shouldCallAuthAPI(requireFresh: boolean = false): boolean {
  // Always call API for security-critical operations
  if (requireFresh) {
    console.log('🔐 Security-critical operation - forcing fresh auth check');
    return true;
  }

  // Use cache if valid
  if (authCache.isValid()) {
    const stats = authCache.getStats();
    console.log(`⚡ Using cached auth (age: ${stats.cacheAge.toFixed(1)}s, expires in: ${stats.timeUntilExpiry.toFixed(1)}s)`);
    return false;
  }

  console.log('📡 Cache invalid - making auth API call');
  return true;
}
